import { useState } from "react";
import { Loader2, Play, Volume2, VolumeX, X } from "lucide-react";
import { useLumiHolocard } from "@/hooks/useLumiHolocard";
import { instructorPortraitMap } from "@/components/launch/InstructorVoiceCaption";

interface LumiHolocardProps {
  momentId: string;
  /** Optional short label shown as a chip beside "LUMI" (e.g. "NEXT STEP"). */
  chipLabel?: string;
  /** Optional bold title above the caption (typically the lesson name). */
  title?: string;
  size?: "sm" | "md";
  /** Float as a fixed overlay in the lower-left of the viewport. Defaults to true. */
  floating?: boolean;
  className?: string;
}

/**
 * Optional, learner-clicked Lumi support card during lessons.
 *
 * The holocard is not a chatbot — it is an in-world Lunimar Collective
 * device. Tap to hear a short, approved Lumi note. Never auto-plays,
 * never interrupts lesson video narration or another active voice
 * moment, and always shows text even when voice audio is muted.
 */
export default function LumiHolocard({
  momentId,
  chipLabel = "NEXT STEP",
  title,
  size = "md",
  floating = true,
  className = "",
}: LumiHolocardProps) {
  const { moment, status, play, voiceEnabled } = useLumiHolocard(momentId);
  const [dismissed, setDismissed] = useState(false);
  if (!moment || dismissed) return null;

  const portrait = instructorPortraitMap.lumi?.portrait;
  const busy = status === "preparing";
  const speaking = status === "speaking";
  const unavailable = status === "unavailable" || status === "failed";
  const playLabel = speaking
    ? "Lumi is speaking"
    : busy
      ? "Preparing Lumi's voice"
      : unavailable
        ? "Lumi voice unavailable — read transcript"
        : "Hear Lumi";
  const avatarSize = size === "sm" ? "h-11 w-11" : "h-14 w-14";

  const wrapperClass = floating
    ? "fixed bottom-4 left-4 z-40 w-[calc(100%-2rem)] max-w-[22rem] sm:bottom-6 sm:left-6"
    : "relative w-full max-w-[22rem]";

  return (
    <div
      className={`${wrapperClass} isolate overflow-hidden rounded-2xl border border-white/25 bg-white/10 px-4 pb-3.5 pt-3 shadow-[0_20px_60px_-20px_hsl(var(--primary)/0.55),0_1px_0_0_hsl(0_0%_100%/0.4)_inset,0_0_0_1px_hsl(0_0%_100%/0.08)_inset] backdrop-blur-2xl backdrop-saturate-150 ${className}`}
      role="group"
      aria-label="Lumi voice moment"
    >
      {/* Clear glass sheen */}
      <div
        className="pointer-events-none absolute inset-0 -z-10 rounded-2xl bg-gradient-to-br from-white/25 via-white/5 to-transparent"
        aria-hidden
      />
      {/* Top highlight streak */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-16 rounded-t-2xl bg-gradient-to-b from-white/30 to-transparent"
        aria-hidden
      />
      {/* Soft holographic tint */}
      <div
        className="pointer-events-none absolute -left-10 -top-10 -z-10 h-28 w-28 rounded-full bg-primary/25 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -bottom-10 -right-10 -z-10 h-28 w-28 rounded-full bg-accent/20 blur-3xl"
        aria-hidden
      />

      {/* Header row: LUMI + chip + close */}
      <div className="mb-2 flex items-center gap-2">
        <span className="rounded-md bg-primary/90 px-2 py-[3px] font-heading text-[11px] font-extrabold uppercase tracking-[0.28em] text-primary-foreground shadow-[0_0_14px_hsl(var(--primary)/0.65)]">
          LUMI
        </span>
        {chipLabel && (
          <span className="rounded-full border border-accent/40 bg-accent/15 px-2 py-[2px] text-[9px] font-semibold uppercase tracking-[0.2em] text-accent">
            {chipLabel}
          </span>
        )}
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss Lumi card"
          className="ml-auto -mr-1 inline-flex h-7 w-7 items-center justify-center rounded-full border border-primary/25 bg-[hsl(var(--background)/0.6)] text-foreground/70 transition hover:border-primary/50 hover:bg-primary/15 hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          <div
            className="absolute -inset-1 rounded-full bg-gradient-to-br from-primary/60 to-accent/60 opacity-80 blur-md"
            aria-hidden
          />
          {portrait ? (
            <img
              src={portrait}
              alt="Lumi Vanta"
              className={`relative rounded-full border-2 border-primary/60 object-cover ${avatarSize}`}
            />
          ) : (
            <div
              className={`relative rounded-full border-2 border-primary/60 bg-gradient-to-br from-primary/50 to-accent/50 ${avatarSize}`}
            />
          )}
        </div>

        <div className="min-w-0 flex-1 pt-0.5">
          {title && (
            <div className="font-heading text-[15px] font-bold leading-tight text-foreground">
              {title}
            </div>
          )}
          <p
            className="mt-1 text-[13px] leading-snug text-foreground/95"
            aria-live="polite"
          >
            {moment.transcript}
          </p>
          {!voiceEnabled && (
            <p className="mt-1 text-[11px] text-muted-foreground">Voice is muted — reading transcript.</p>
          )}
          {unavailable && voiceEnabled && (
            <p className="mt-1 text-[11px] text-muted-foreground">Voice unavailable — reading transcript.</p>
          )}
        </div>

        <button
          type="button"
          onClick={play}
          disabled={busy || speaking}
          aria-label={playLabel}
          title={playLabel}
          className="relative inline-flex h-11 w-11 shrink-0 items-center justify-center self-center rounded-full border-2 border-primary/60 bg-primary text-primary-foreground shadow-[0_0_18px_hsl(var(--primary)/0.55)] transition hover:scale-105 hover:bg-primary/90 disabled:opacity-70"
        >
          {busy ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : speaking ? (
            <Volume2 className="h-5 w-5" />
          ) : unavailable ? (
            <VolumeX className="h-5 w-5" />
          ) : (
            <Play className="ml-0.5 h-5 w-5 fill-current" />
          )}
        </button>
      </div>
    </div>
  );
}